<template>
    <BaseDialog v-model:open="open" :title="title" :disable-portal="disablePortal" :as-child="asChild">
        <div class="flex flex-col justify-start items-start p-6 gap-5 md:p-10 md:gap-8 relative" :class="containerClass">
            <BaseButton theme="neutral" class="border-none absolute right-6 top-6 px-3" data-testid="close-button" aria-label="Close dialog" @click="onCancelClicked">
                <i class="bi bi-x text-3xl text-[#939292]" aria-hidden="true"></i>
            </BaseButton>
            <slot name="icon"></slot>
            <slot name="title">
                <h2 v-if="title" class="title pr-12" data-testid="dialog-title">{{ title }}</h2>
            </slot>
            <div v-if="text" class="body text-[#595959] select-all" data-testid="dialog-text">{{ text }}</div>
            <slot name="content"></slot>
            <div class="flex flex-col-reverse gap-3 w-full md:flex-row md:justify-between md:items-center md:gap-8">
                <BaseButton theme="tertiary" class="body py-2 min-w-35" data-testid="cancel-button" @click="onCancelClicked">{{ cancelLabel }}</BaseButton>
                <BaseButton :theme="confirmTheme" class="body whitespace-nowrap py-2 min-w-35" :disabled="confirmDisabled" data-testid="confirm-button" @click="onConfirmClicked">
                    {{ confirmLabel }}
                </BaseButton>
            </div>
        </div>
    </BaseDialog>
</template>

<script setup lang="ts">
import type { ButtonTheme } from "./BaseButton.vue";
import BaseButton from "./BaseButton.vue";
import BaseDialog from "./BaseDialog.vue";

type ConfirmationDialogProps = {
    title?: string;
    text?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    // Theme for the confirm button; use "danger" for destructive confirmations.
    confirmTheme?: ButtonTheme;
    disablePortal?: boolean;
    asChild?: boolean;
    // Disables the confirm button (e.g. while a required input is invalid or a request is in flight).
    confirmDisabled?: boolean;
    // When false, the dialog stays open after confirm so the parent can run async work and close it itself.
    closeOnConfirm?: boolean;
    // uses props over :deep() selectors since this element is portaled to body
    containerClass?: string | Record<string, unknown> | unknown[];
};

const {
    title = "",
    text = "",
    confirmLabel = "OK",
    cancelLabel = "Cancel",
    confirmTheme = "primary",
    disablePortal = false,
    asChild = true,
    confirmDisabled = false,
    closeOnConfirm = true,
    containerClass,
} = defineProps<ConfirmationDialogProps>();

const emit = defineEmits<{
    confirm: [];
    cancel: [];
}>();

const open = defineModel<boolean>("open", {
    type: Boolean,
    default: false,
});

const onConfirmClicked = () => {
    if (confirmDisabled) {
        return;
    }
    if (closeOnConfirm) {
        open.value = false;
    }
    emit("confirm");
};

const onCancelClicked = () => {
    open.value = false;
    emit("cancel");
};
</script>
